import React, { Fragment, useState } from 'react';

import classNames from 'classnames';

import TimeWeekStatusPill from '../my_time_record/TimeWeekStatusPill';

import { formatCellValue, formatTotalValue, UNIT_HOURS } from './summaryUnits';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

/**
 * Weeks across, project › role down. Each project row is a subtotal that folds
 * its roles away; a role with nothing in a week shows a dash rather than a zero.
 * The last row links each week to My time record — View for a submitted week,
 * Edit otherwise. The current week's column is tinted.
 *
 * The total and link rows sit in the body rather than a tfoot: the shared table
 * styles paint every tfoot row blue, which hid the links.
 */
export default function TimeSummaryTable({ weeks, projects, grandTotal, unit, hoursInDay }) {
    const [collapsed, setCollapsed] = useState({});

    const cell = (days) => formatCellValue(days, unit, hoursInDay);
    const toggle = (id) => setCollapsed((previous) => ({ ...previous, [id]: !previous[id] }));
    const unitWord = unit === UNIT_HOURS ? $t('hours') : $t('days');
    // The server says which week is current, so the tint follows its today
    // rather than the browser's. Important because the shared table styles give
    // every body cell the row's background with a more specific selector.
    const isCurrent = (week) => !!week.is_current;
    const columnTint = (week) => ({ '!tw-bg-blue-10': isCurrent(week) });

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th className="tw-text-start tw-min-w-56">{$t('Project › role')}</th>
                        {weeks.map((week) => (
                            <th key={week.start} className="tw-text-end tw-min-w-24">
                                <a href={week.week_url} className="tw-block tw-text-inherit tw-no-underline hover:tw-text-blue hover:tw-no-underline">
                                    <div>{$t('W{{number}}', { number: week.number })}</div>
                                    <div className="tw-text-xs tw-font-normal">{week.label}</div>
                                </a>
                            </th>
                        ))}
                        <th className="tw-text-end tw-min-w-20">
                            <div>{$t('Total')}</div>
                            <div className="tw-text-xs tw-font-normal">{unitWord}</div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="tw-text-charcoal-50">{$t('Status')}</td>
                        {weeks.map((week) => (
                            <td key={week.start} className={classNames('tw-text-end', columnTint(week))}>
                                <TimeWeekStatusPill status={week.status} size="sm" />
                            </td>
                        ))}
                        <td />
                    </tr>

                    {projects.map((project) => {
                        const isCollapsed = !!collapsed[project.id];
                        const Chevron = isCollapsed ? ChevronRightIcon : ChevronDownIcon;

                        return (
                            <Fragment key={project.id}>
                                <tr className="tw-font-semibold">
                                    <td className="!tw-bg-charcoal-10">
                                        <button
                                            type="button"
                                            className="tw-inline-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-charcoal"
                                            aria-expanded={!isCollapsed}
                                            onClick={() => toggle(project.id)}
                                        >
                                            <Chevron className="tw-h-3 tw-w-3 tw-text-charcoal-50" />
                                            {project.name}
                                        </button>
                                    </td>
                                    {weeks.map((week) => (
                                        <td key={week.start} className="inbud-numeric-cell !tw-bg-charcoal-10">
                                            {cell(project.totals[week.start])}
                                        </td>
                                    ))}
                                    <td className="inbud-numeric-cell !tw-bg-charcoal-10">{cell(project.total)}</td>
                                </tr>

                                {!isCollapsed &&
                                    project.roles.map((role) => (
                                        <tr key={role.key}>
                                            <td className="tw-ps-8">
                                                {role.role_name} <span className="tw-text-xs tw-text-charcoal-50">{role.category}</span>
                                            </td>
                                            {weeks.map((week) => (
                                                <td
                                                    key={week.start}
                                                    className={classNames('inbud-numeric-cell', columnTint(week), {
                                                        'tw-text-charcoal-50': role.totals[week.start] === null,
                                                    })}
                                                >
                                                    {cell(role.totals[week.start])}
                                                </td>
                                            ))}
                                            <td className="inbud-numeric-cell">{cell(role.total)}</td>
                                        </tr>
                                    ))}
                            </Fragment>
                        );
                    })}

                    <tr className="tw-font-semibold">
                        <td className="!tw-bg-blue !tw-text-white">{unit === UNIT_HOURS ? $t('Total hours') : $t('Total days')}</td>
                        {weeks.map((week) => (
                            <td key={week.start} className="inbud-numeric-cell !tw-bg-blue !tw-text-white">
                                {formatTotalValue(week.total, unit, hoursInDay)}
                            </td>
                        ))}
                        <td className="inbud-numeric-cell !tw-bg-blue !tw-text-white">{formatTotalValue(grandTotal, unit, hoursInDay)}</td>
                    </tr>
                    <tr>
                        <td className="!tw-bg-white tw-text-charcoal-50">{$t('Open week')}</td>
                        {weeks.map((week) => (
                            <td key={week.start} className={classNames('tw-text-end', isCurrent(week) ? '!tw-bg-blue-10' : '!tw-bg-white')}>
                                <a href={week.week_url} className="tw-font-semibold tw-text-blue">
                                    {week.status === 'submitted' ? $t('View') : $t('Edit')}
                                </a>
                            </td>
                        ))}
                        <td className="!tw-bg-white" />
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
